import { get } from "../../../core/api/client";
import { API_ENDPOINTS } from "../../../core/api/endpoints";

const STATUS_ORDER = [
  "Operational",
  "Partial Issues",
  "Experiencing Delays",
  "Down",
];

const resolveUpdatedAt = (response) =>
  response?.details?.updatedAt ||
  response?.details?.serverTime ||
  response?.details?.timestamp ||
  response?.updatedAt ||
  response?.timestamp ||
  new Date().toISOString();

const normalizeStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) {
    return "Operational";
  }

  if (
    normalized.includes("down") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return "Down";
  }

  if (normalized.includes("partial")) {
    return "Partial Issues";
  }

  if (normalized.includes("delay") || normalized.includes("slow")) {
    return "Experiencing Delays";
  }

  return "Operational";
};

const buildNotes = (value, fallback = "") => {
  const notes = String(value || fallback || "").trim();
  return notes || "No issues reported.";
};

const shortNoteByServiceAndStatus = {
  "API Server": {
    Operational: "API server is reachable.",
    "Partial Issues": "Experiencing delays.",
    "Experiencing Delays": "Experiencing delays.",
    Down: "Service unavailable.",
  },
  Database: {
    Operational: "Database running smoothly.",
    "Partial Issues": "Experiencing delays.",
    "Experiencing Delays": "Experiencing delays.",
    Down: "Server maintenance.",
  },
  "Email Service": {
    Operational: "Email Service working normally.",
    "Partial Issues": "Some delivery delays.",
    "Experiencing Delays": "Some delivery delays.",
    Down: "Server maintenance.",
  },
  "File Storage": {
    Operational: "File Storage working properly.",
    "Partial Issues": "Uploads slower than usual.",
    "Experiencing Delays": "Uploads slower than usual.",
    Down: "Server maintenance.",
  },
  "Notification Service": {
    Operational: "Notification service working normally.",
    "Partial Issues": "Some delivery delays.",
    "Experiencing Delays": "Some delivery delays.",
    Down: "Server maintenance.",
  },
  "Payment Gateway": {
    Operational: "Payment gateway service working normally.",
    "Partial Issues": "Some transactions delayed.",
    "Experiencing Delays": "Some transactions delayed.",
    Down: "Server maintenance.",
  },
  "CDN Service": {
    Operational: "CDN service running smoothly.",
    "Partial Issues": "Content delivery slower.",
    "Experiencing Delays": "Content delivery slower.",
    Down: "Server maintenance.",
  },
};

const getShortNote = (serviceName, status) => {
  const serviceNotes = shortNoteByServiceAndStatus[serviceName] || {};
  return serviceNotes[status] || serviceNotes.Operational || "No issues reported.";
};

const getServiceStatus = (response, { statusResolver, notesResolver }) => {
  if (response?.error) {
    return {
      status: "Down",
      notes: response.error.message || "Unable to load service status.",
    };
  }

  const status = statusResolver ? statusResolver(response) : normalizeStatus(response?.status);
  const notes = notesResolver ? notesResolver(response) : buildNotes(response?.message, response?.details?.message);

  return {
    status,
    notes,
  };
};

const serviceDefinitions = [
  {
    key: "apiServer",
    serviceName: "API Server",
    endpoint: API_ENDPOINTS.HEALTH.SERVICE,
    statusResolver: (response) => normalizeStatus(response?.status),
    notesResolver: (_, status) => getShortNote("API Server", status),
  },
  {
    key: "database",
    serviceName: "Database",
    endpoint: API_ENDPOINTS.HEALTH.DATABASE,
    statusResolver: (response) =>
      response?.status === "ok" &&
      String(response?.details?.database || "").toLowerCase() === "connected"
        ? "Operational"
        : normalizeStatus(response?.status || response?.details?.database),
    notesResolver: (_, status) => getShortNote("Database", status),
  },
  {
    key: "emailService",
    serviceName: "Email Service",
    endpoint: API_ENDPOINTS.HEALTH.EMAIL,
    statusResolver: (response) =>
      response?.details?.sendingEnabled === true
        ? "Operational"
        : response?.status === "ok"
          ? "Partial Issues"
          : normalizeStatus(response?.status),
    notesResolver: (_, status) => getShortNote("Email Service", status),
  },
  {
    key: "fileStorage",
    serviceName: "File Storage",
    endpoint: API_ENDPOINTS.HEALTH.FILE_STORAGE,
    statusResolver: (response) => {
      const filesLength = Number(response?.details?.recentFilesLength || 0);
      if (response?.status !== "ok") return normalizeStatus(response?.status);
      return filesLength > 0 ? "Operational" : "Experiencing Delays";
    },
    notesResolver: (_, status) => getShortNote("File Storage", status),
  },
  {
    key: "notificationService",
    serviceName: "Notification Service",
    endpoint: API_ENDPOINTS.HEALTH.NOTIFICATION,
    statusResolver: (response) =>
      response?.details?.sendingEnabled === true
        ? "Operational"
        : response?.status === "ok"
          ? "Partial Issues"
          : normalizeStatus(response?.status),
    notesResolver: (_, status) => getShortNote("Notification Service", status),
  },
  {
    key: "paymentGateway",
    serviceName: "Payment Gateway",
    endpoint: API_ENDPOINTS.HEALTH.STRIPE,
    statusResolver: (response) =>
      response?.details?.isOperational === true
        ? "Operational"
        : response?.status === "ok"
          ? "Partial Issues"
          : normalizeStatus(response?.status),
    notesResolver: (_, status) => getShortNote("Payment Gateway", status),
  },
  {
    key: "cdnService",
    serviceName: "CDN Service",
    endpoint: API_ENDPOINTS.HEALTH.CDN,
    statusResolver: (response) =>
      response?.details?.isOperational === true
        ? "Operational"
        : response?.status === "ok"
          ? "Partial Issues"
          : normalizeStatus(response?.status),
    notesResolver: (_, status) => getShortNote("CDN Service", status),
  },
];

export const getSystemStatusServices = () => serviceDefinitions.slice();

export const fetchSystemStatusServices = async () => {
  const responses = await Promise.all(
    serviceDefinitions.map(async (service) => {
      const response = await get(service.endpoint);
      const status = service.statusResolver ? service.statusResolver(response) : normalizeStatus(response?.status);
      const normalized = getServiceStatus(response, {
        statusResolver: service.statusResolver,
        notesResolver: (serviceResponse) => service.notesResolver(serviceResponse, status),
      });

      return {
        serviceName: service.serviceName,
        key: service.key,
        endpoint: service.endpoint,
        lastUpdated: resolveUpdatedAt(response),
        status: normalized.status,
        notes: normalized.notes,
        raw: response,
      };
    }),
  );

  return responses.sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  );
};
